import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CheckCircle2, XCircle, Send, Calendar, Edit2, Clock, AlertTriangle, MessageCircle, Mail, Sparkles, RefreshCw } from "lucide-react";
import Modal from "@/components/Modal";
import { toast } from "sonner";
import { gmailComposeUrl, whatsappUrl } from "@/lib/outreach";

const STATUS_META = {
  pending: { label: "Awaiting approval", bg: "#FFF7EC", color: "#92400E", icon: Clock },
  approved: { label: "Approved · queued", bg: "#F0F4F8", color: "#1E3A5F", icon: CheckCircle2 },
  sent: { label: "Sent", bg: "#EAF0EA", color: "#2F4F4F", icon: Send },
  cancelled: { label: "Cancelled", bg: "#F2EFEA", color: "#6B6965", icon: XCircle },
  failed: { label: "Failed", bg: "#F5EDED", color: "#B91C1C", icon: AlertTriangle },
};

function formatWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const now = new Date();
  const ms = d - now;
  const days = Math.round(ms / (1000 * 60 * 60 * 24));
  if (ms < 0) {
    if (days === 0) return "Due now";
    return `${Math.abs(days)}d overdue · ${d.toLocaleDateString()}`;
  }
  if (days === 0) return `Today · ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
  if (days === 1) return `Tomorrow · ${d.toLocaleDateString()}`;
  return `in ${days}d · ${d.toLocaleDateString()}`;
}

function EditModal({ followup, onClose, onSaved }) {
  const [scheduledAt, setScheduledAt] = useState(followup.scheduled_at?.slice(0, 16) || "");
  const [message, setMessage] = useState(followup.message || "");
  const [subject, setSubject] = useState(followup.subject || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const iso = new Date(scheduledAt).toISOString();
      await api.patch(`/followups/${followup.id}`, { scheduled_at: iso, message, subject });
      toast.success("Follow-up updated");
      onSaved();
    } catch {
      toast.error("Update failed");
    } finally { setSaving(false); }
  };

  return (
    <Modal title="Edit Follow-up" subtitle={`To ${followup.customer_snapshot?.company}`} onClose={onClose} size="lg">
      <div data-testid="edit-followup-modal" className="grid gap-4">
        <div>
          <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>When to send</label>
          <input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} className="input-base font-mono-data" data-testid="edit-scheduled-at" />
        </div>
        {followup.channel === "email" && (
          <div>
            <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>Subject</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} className="input-base" data-testid="edit-subject" />
          </div>
        )}
        <div>
          <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>Message</label>
          <textarea rows={followup.channel === "email" ? 10 : 6} value={message} onChange={(e) => setMessage(e.target.value)} className="input-base" style={{ resize: "vertical", fontFamily: "inherit" }} data-testid="edit-message" />
        </div>
        <div className="flex justify-end gap-3 mt-2">
          <button onClick={onClose} className="btn-outline" data-testid="edit-cancel">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-primary" data-testid="edit-save">{saving ? "Saving…" : "Save Changes"}</button>
        </div>
      </div>
    </Modal>
  );
}

export default function Followups() {
  const [followups, setFollowups] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all"); // all | pending | approved | sent | cancelled | failed
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [f, s] = await Promise.all([api.get("/followups"), api.get("/followups-stats")]);
    setFollowups(f.data);
    setStats(s.data);
  };

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  const filtered = followups.filter((f) => filter === "all" || f.status === filter);

  const approve = async (f) => {
    await api.patch(`/followups/${f.id}`, { status: "approved" });
    toast.success("Approved — will auto-send when due");
    load();
  };
  const cancel = async (f) => {
    if (!window.confirm("Cancel this follow-up?")) return;
    await api.patch(`/followups/${f.id}`, { status: "cancelled" });
    toast.success("Cancelled");
    load();
  };
  const sendNow = async (f) => {
    const isEmail = f.channel === "email";
    const to = isEmail ? f.customer_snapshot?.email : f.customer_snapshot?.phone;
    if (!to) {
      toast.error(`No ${isEmail ? "email" : "phone"} on file for ${f.customer_snapshot?.company || "this customer"}`);
      return;
    }
    // Opens a prefilled Gmail compose tab / wa.me chat for the rep to send from
    // their own account — same client-side pattern as the rest of the app, no
    // provider credentials involved.
    const url = isEmail ? gmailComposeUrl({ to, subject: f.subject, body: f.message }) : whatsappUrl({ to, body: f.message });
    window.open(url, "_blank", "noopener,noreferrer");
    try {
      await api.post(`/followups/${f.id}/send-now`);
      toast.success(`Opened ${isEmail ? "Gmail" : "WhatsApp"} — marked as sent`);
    } catch (e) {
      const detail = e.response?.data?.detail || "Couldn't mark as sent";
      toast.error("Opened the compose window, but marking as sent failed", { description: detail, duration: 6000 });
    }
    load();
  };
  const retryFailed = async (f) => {
    await api.patch(`/followups/${f.id}`, { status: "approved" });
    toast.success("Re-queued for sending");
    load();
  };

  return (
    <div data-testid="followups-page">
      {/* Hero stats */}
      <div className="card-ledger stagger mb-6" style={{ padding: 24, display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 32, alignItems: "center" }}>
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={16} style={{ color: "var(--primary)" }} />
            <div className="overline">Follow-ups</div>
          </div>
          <div style={{ fontSize: 15, color: "var(--text)", lineHeight: 1.5, maxWidth: 560 }}>
            Hit <strong>Follow up</strong> on any lead to schedule a stage-aware nudge — after a quote, samples, or an approval. When a queued follow-up is due, hit <strong>Send Now</strong> to open Gmail or WhatsApp with the message ready to go; edit or cancel any of them here.
          </div>
        </div>
        {stats && (
          <div className="grid gap-3" style={{ gridTemplateColumns: "1fr 1fr 1fr" }}>
            {[["Needs Attention", stats.needs_attention, "var(--primary)"], ["Queued", stats.approved, "#1E3A5F"], ["Sent", stats.sent, "var(--success)"]].map(([l, v, c]) => (
              <div key={l} style={{ borderLeft: `3px solid ${c}`, paddingLeft: 12 }}>
                <div className="overline" style={{ fontSize: 9 }}>{l}</div>
                <div className="font-mono-data" style={{ fontSize: 28, fontWeight: 700, color: c }}>{v}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap mb-4" data-testid="followup-filters">
        {["all", "pending", "approved", "sent", "cancelled", "failed"].map((k) => (
          <button key={k} onClick={() => setFilter(k)} className={filter === k ? "btn-primary" : "btn-outline"} style={{ padding: "7px 14px", fontSize: 13, textTransform: "capitalize" }} data-testid={`fu-filter-${k}`}>
            {k} {stats && k !== "all" ? `(${stats[k] ?? 0})` : ""}
          </button>
        ))}
        <button onClick={load} className="btn-ghost ml-auto" data-testid="fu-refresh"><RefreshCw size={14} /> Refresh</button>
      </div>

      {filtered.length === 0 ? (
        <div className="card-ledger" style={{ padding: 48, textAlign: "center", color: "var(--text-2)" }} data-testid="followups-empty">
          <Sparkles size={28} style={{ margin: "0 auto 12px", color: "var(--text-2)" }} />
          <div style={{ fontSize: 14 }}>{filter === "all" ? "No follow-ups yet. Hit “Follow up” on a lead to schedule one." : `No ${filter} follow-ups.`}</div>
        </div>
      ) : (
        <div className="grid gap-3" data-testid="followups-list">
          {filtered.map((f) => {
            const meta = STATUS_META[f.status] || STATUS_META.pending;
            const Icon = meta.icon;
            const ChannelIcon = f.channel === "whatsapp" ? MessageCircle : Mail;
            return (
              <div key={f.id} className="card-ledger" style={{ padding: 18, display: "grid", gridTemplateColumns: "auto 1fr auto", gap: 16, alignItems: "start" }} data-testid={`followup-${f.id}`}>
                <div style={{ width: 38, height: 38, borderRadius: 6, background: meta.bg, color: meta.color, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <ChannelIcon size={18} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div className="flex items-center gap-3 flex-wrap mb-1">
                    <span style={{ fontWeight: 700, fontSize: 14 }}>{f.customer_snapshot?.company || "Unknown"}</span>
                    <span className="tag" style={{ background: meta.bg, color: meta.color, borderColor: meta.bg }} data-testid={`status-${f.status}`}>
                      <Icon size={11} /> {meta.label}
                    </span>
                    <span style={{ fontSize: 12, color: "var(--text-2)", display: "flex", alignItems: "center", gap: 4 }}>
                      <Calendar size={11} /> {formatWhen(f.scheduled_at)}
                    </span>
                    <span style={{ fontSize: 11, color: "var(--text-2)", textTransform: "uppercase", letterSpacing: 1 }}>via {f.channel}</span>
                  </div>
                  <div style={{ fontSize: 13, color: "var(--text-2)", whiteSpace: "pre-wrap", lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{f.message}</div>
                  {f.error && (
                    <div style={{ fontSize: 12, color: "var(--danger)", marginTop: 6, padding: "4px 8px", background: "#FCE8E8", borderRadius: 4 }} data-testid={`error-${f.id}`}>
                      <AlertTriangle size={11} style={{ display: "inline", marginRight: 4 }} /> {f.error}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2" style={{ alignItems: "flex-end" }}>
                  {f.status === "pending" && (
                    <>
                      <button onClick={() => approve(f)} className="btn-primary" style={{ padding: "6px 12px", fontSize: 12 }} data-testid={`approve-${f.id}`}>
                        <CheckCircle2 size={13} /> Approve
                      </button>
                      <div className="flex gap-1">
                        <button onClick={() => setEditing(f)} className="btn-ghost" title="Edit" data-testid={`edit-${f.id}`}><Edit2 size={13} /></button>
                        <button onClick={() => cancel(f)} className="btn-ghost" style={{ color: "var(--danger)" }} title="Cancel" data-testid={`cancel-${f.id}`}><XCircle size={13} /></button>
                      </div>
                    </>
                  )}
                  {f.status === "approved" && (
                    <>
                      <button onClick={() => sendNow(f)} className="btn-outline" style={{ padding: "6px 12px", fontSize: 12 }} data-testid={`send-now-${f.id}`}>
                        <Send size={13} /> Send Now
                      </button>
                      <div className="flex gap-1">
                        <button onClick={() => setEditing(f)} className="btn-ghost" title="Edit" data-testid={`edit-${f.id}`}><Edit2 size={13} /></button>
                        <button onClick={() => cancel(f)} className="btn-ghost" style={{ color: "var(--danger)" }} title="Cancel" data-testid={`cancel-${f.id}`}><XCircle size={13} /></button>
                      </div>
                    </>
                  )}
                  {f.status === "failed" && (
                    <button onClick={() => retryFailed(f)} className="btn-outline" style={{ padding: "6px 12px", fontSize: 12 }} data-testid={`retry-${f.id}`}>
                      <RefreshCw size={13} /> Retry
                    </button>
                  )}
                  {f.status === "sent" && f.sent_at && (
                    <div style={{ fontSize: 11, color: "var(--text-2)" }} className="font-mono-data">{new Date(f.sent_at).toLocaleString()}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <EditModal
          followup={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}
